/**
 * NuGuard AI - OAuth Callback Handler
 *
 * Handles the redirect from OAuth providers.
 * Extracts tokens from URL params, stores them, and redirects to dashboard.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { authApi, tokenManager } from '../services/api';
import { User } from '../types';
import { getErrorMessage } from '../utils/errorHandler';

interface OAuthCallbackProps {
  onLogin: (user: User) => void;
}

const ERROR_MESSAGES: Record<string, string> = {
  oauth_failed: 'Authentication with Google failed. Please try again.',
  oauth_state_expired: 'The sign-in session timed out (took more than 5 minutes). Please try again.',
  oauth_state_invalid: 'Invalid sign-in state — this can happen if your browser blocked cookies or a proxy modified the request. Try again in an incognito/private window.',
  oauth_token_exchange: 'The server could not complete sign-in with Google. An administrator should verify that OAUTH_GOOGLE_CLIENT_SECRET and OAUTH_REDIRECT_BASE_URL are correctly set and that the redirect URI is registered in Google Cloud Console.',
  oauth_userinfo: 'Unable to retrieve your Google profile. Please try again.',
  no_email: 'No verified email found on your Google account. Please add a verified email and try again.',
  account_disabled: 'Your account has been disabled. Contact your administrator.',
};

const OAuthCallback: React.FC<OAuthCallbackProps> = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Set when this callback is the result of clicking an email verification link
  // (vs. an OAuth provider login) so we can show a confirmation instead of
  // silently finishing sign-in (#873).
  const [showVerifiedConfirmation, setShowVerifiedConfirmation] = useState(false);

  useEffect(() => {
    // Strip tokens from the URL immediately so they never sit in browser
    // history or get captured by PostHog's pageview autocapture ($current_url).
    if (searchParams.get('access_token') || searchParams.get('refresh_token')) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    const handleCallback = async () => {
      // Check for error
      const errorParam = searchParams.get('error');
      if (errorParam) {
        setError(ERROR_MESSAGES[errorParam] || `Authentication error: ${errorParam}`);
        return;
      }

      const isEmailVerification = searchParams.get('verified') === '1';

      // Extract tokens
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const expiresIn = searchParams.get('expires_in');

      if (!accessToken || !refreshToken) {
        setError('Invalid callback — missing tokens. Please try signing in again.');
        return;
      }

      try {
        // Store tokens using the existing token manager
        // Access the internal token manager via the api module
        const tokenData = {
          access_token: accessToken,
          refresh_token: refreshToken,
          token_type: 'bearer',
          expires_in: parseInt(expiresIn || '1800', 10),
        };

        // Store tokens via TokenManager (updates both in-memory and sessionStorage)
        tokenManager.setTokens(
          tokenData.access_token,
          tokenData.refresh_token,
          tokenData.expires_in
        );

        // Fetch current user
        const currentUser = await authApi.getCurrentUser();

        const adaptedUser: User = {
          id: currentUser.id,
          email: currentUser.email,
          name: currentUser.name,
          role: currentUser.role,
          scope: currentUser.scope || 'tenant',
          avatar: currentUser.avatar || undefined,
          tenant_name: currentUser.tenant_name || null,
          tenant_id: currentUser.tenant_id,
          is_active: currentUser.is_active ?? true,
          created_at: currentUser.created_at || new Date().toISOString(),
        };

        onLogin(adaptedUser);

        if (isEmailVerification) {
          // Show an explicit confirmation instead of silently dropping the
          // user onto the dashboard (or, if anything above hiccups, back at
          // the login screen with no explanation).
          setShowVerifiedConfirmation(true);
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to complete sign-in. Please try again.'));
      }
    };

    handleCallback();
  }, [searchParams, onLogin, navigate]);

  if (showVerifiedConfirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-nuguard-bg">
        <div className="w-full max-w-md p-8">
          <div className="bg-white dark:bg-nuguard-card rounded-2xl shadow-xl border border-stone-200 dark:border-nuguard-edge/60 p-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-stone-900 dark:text-white mb-2">
              Email Verified
            </h2>
            <p className="text-stone-600 dark:text-slate-400 mb-6">
              Your account is now active and you're signed in.
            </p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-nuguard-bg">
        <div className="w-full max-w-md p-8">
          <div className="bg-white dark:bg-nuguard-card rounded-2xl shadow-xl border border-stone-200 dark:border-nuguard-edge/60 p-8 text-center">
            <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-stone-900 dark:text-white mb-2">
              Sign-in Failed
            </h2>
            <p className="text-stone-600 dark:text-slate-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-nuguard-bg">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        <span className="text-stone-500 dark:text-slate-400">Completing sign-in...</span>
      </div>
    </div>
  );
};

export default OAuthCallback;
